<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useTranslaterStore }   from '@/stores/translater'
import { useProfileStore }      from '@/stores/profile'
import { useCarsStore }         from '@/stores/cars'
import { useWsStore }           from '@/stores/ws'
import { useWsSubsStore }       from '@/stores/wsSubs'
import { usePointsStore }       from '@/stores/points'
import { useFavoriteCarsStore } from '@/stores/favorite-cars'
import checkArrayIncludes       from '@/helpers/checkArrayIncludes'

import Modal            from '@/components/UTAModal.vue'
import UTACarsCreate    from '@/components/UTACarsCreate.vue'
import UTACarImage      from '@/components/car/UTACarImage.vue'
import vinDecoder       from '@/helpers/vinDecoder'

const translaterStore   = useTranslaterStore()
const profileStore      = useProfileStore()
const carsStore         = useCarsStore()
const wsStore           = useWsStore()
const wsSubsStore       = useWsSubsStore()
const pointsStore       = usePointsStore()
const favoriteCarsStore = useFavoriteCarsStore()

const getTranslate          = computed(() => translaterStore.getTranslate)
const profile               = computed(() => profileStore.profile)
const cars                  = computed(() => carsStore.cars)
const carsMsg               = computed(() => carsStore.carsMsg)
const carsProcess           = computed(() => carsStore.carsProcess)
const carsActivateMsg       = computed(() => carsStore.carsActivateMsg)
const favoriteCarCreateMsg  = computed(() => favoriteCarsStore.favoriteCarCreateMsg)
const favoriteCarDeleteMsg  = computed(() => favoriteCarsStore.favoriteCarDeleteMsg)

const pageNumber        = ref(1)
const viewMode          = ref(localStorage.getItem('UTA_CARS_VIEW') || 'grid')
const showFavorites     = ref(JSON.parse(localStorage.getItem('UTA_CARS_FAVORITES')) || false)
const showCarsCreate    = ref(false)
const searchMode        = ref(null)
const searchModeText    = ref('CARS_SEARCH_MODE_ALL')
const inputSearch       = ref('')
const carsUuid          = ref([])
const onlyOnline        = ref(JSON.parse(localStorage.getItem('UTA_CARS_ONLINE')) || false)

function changePage(cursor, dir) {

    const params = {

        'c.limit': 12, 
        'c.direction': dir, 
        'c.cursor': cursor
    }

    fetchCars(params).then(cars => {

        if(cars.value.array) {

            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

function carStatus(code) {
    let carOnline = { text: 'OFFLINE', status: 0 }
    switch(Number(code)) {
        case 0: 
            carOnline.text = 'OFFLINE'
            carOnline.status = 0
            break
        case 1: 
            carOnline.text = 'ONLINE'
            carOnline.status = 1
            break
        case 2: 
            carOnline.text = 'ONLINE'
            carOnline.status = 2
            break
    }
    return carOnline
}

async function fetchCars(outerParams) {

    let params = {

        'c.limit': 12
    }

    if(inputSearch.value.length > 2)
        params['c.q'] = inputSearch.value

    if(searchMode.value) 
        params['c.filterQ'] = searchMode.value

    if(onlyOnline.value) 
        params.online = true

    if (showFavorites.value)
        params.favorites = true

    if(outerParams) 
        params = { ...params, ...outerParams }

    carsStore.readCars(params).then(responseCars => {

        if(responseCars && responseCars.array?.length) {

            carsUuid.value = []
            responseCars.array.map(car => carsUuid.value.push(car.uuid))

            wsSubsStore.subCars({ cars: carsUuid.value, states: ['online'] })
            wsStore.wsCurrentCars = carsUuid.value

            pointsStore.readLatestPoint({ uuid_car_array: carsUuid.value }).then(points => {
                if(points.array) {
                    responseCars.array.map( car => {
                        car.online = { text: 'OFFLINE', status: 0 }
                        points.array.map(point => {
                            if(point.car == car.uuid) {
                                point.s.map(state => {
                                    if(state.key == 'online') car.online = carStatus(state.value?.i)
                                })
                            }
                        })
                    })
                }
            })
        }
    })

    return cars
}

function toggleFavorites() {

    showFavorites.value = !showFavorites.value
    localStorage.setItem('UTA_CARS_FAVORITES', showFavorites.value)
}

function setSearchMode(mode) {

    if(mode) {

        searchMode.value = mode

        switch(mode) {

            case 'vin': 
                searchModeText.value = 'CARS_SEARCH_MODE_VIN'
                break

            case 'tid': 
                searchModeText.value = 'CARS_SEARCH_MODE_TID'
                break

            case 'imei': 
                searchModeText.value = 'CARS_SEARCH_MODE_IMEI'
                break

            case 'car_number': 
                searchModeText.value = 'CARS_SEARCH_MODE_NUMBER'
                break

            case 'nami_number': 
                searchModeText.value = 'CARS_SEARCH_MODE_NAMI'
                break

            case 'phone': 
                searchModeText.value = 'CARS_SEARCH_MODE_PHONE'
                break

            case 'iccid': 
                searchModeText.value = 'CARS_SEARCH_MODE_ICCID'
                break
        }

    } else {

        searchMode.value = null
        searchModeText.value = 'CARS_SEARCH_MODE_ALL'
    }

    fetchCars()
}

function setViewMode(mode) {

    viewMode.value = mode
    localStorage.setItem('UTA_CARS_VIEW', mode)
}

fetchCars()

onBeforeUnmount(() => {

    wsSubsStore.unsubCars({ cars: carsUuid.value })
    pointsStore.resetCarState()
    pointsStore.resetConnectionType()
})

watch(inputSearch, () => {

    pageNumber.value = 1
    fetchCars()
})

watch(showFavorites, () => {

    pageNumber.value = 1
    fetchCars()
})

watch(carsActivateMsg, msg => {

    if('success' in msg && msg.success) {
        showCarsCreate.value = false
        pageNumber.value = 1
        fetchCars()
    }
})

watch(onlyOnline, (newVal) => {
    localStorage.setItem('UTA_CARS_ONLINE', newVal)

    pageNumber.value = 1
    fetchCars()
})

</script>

<template>
    <div class="my-4 uta-cars">
        <div class="container-fluid">
            <header class="uta-header" role="heading" aria-level="1">
                <h1 class="uta-header__title">{{ getTranslate.CARS_TITLE }}</h1>
                <div class="spinner-border uta-header__spinner" role="status" v-if="carsProcess">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <div class="uta-header__actions">
                    <a 
                        href="#"
                        class="d-md-none uta-btn uta-btn--has-icon uta-btn--sm uta-btn--light uta-header__action uta-header__action--online m-0"
                        @click="onlyOnline = !onlyOnline"
                    >
                        <span class="uta-btn__content">
                            <span class="material-symbols-outlined uta-cars__filter-mobile" :class="{ 'uta-cars__filter-mobile--active' : onlyOnline }">
                                sensors
                            </span>
                        </span>
                    </a>
                    <a 
                        href="#"
                        class="uta-btn uta-btn--has-icon uta-btn--sm uta-btn--light uta-header__action uta-header__action--favorites m-0"
                        @click="toggleFavorites"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons" v-if="showFavorites">
                                star
                            </span>
                            <span class="uta-btn__icon material-icons" v-else>
                                star_border
                            </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_FAVORITES }}</span>
                        </span>
                    </a>
                    <a 
                        href="#" 
                        class="uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon uta-header__action uta-header__action--create"
                        @click="showCarsCreate = true"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons">
                                add
                            </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ checkArrayIncludes(profile.attr?.array, ['CREATOR_CAR', 'SUPER_ADMIN']) ? getTranslate.BTN_CREATE : getTranslate.BTN_ADD }}</span>
                        </span>
                    </a>
                </div>
            </header>

            <div class="row mb-4 justify-content-between">
                <div class="col-12 col-md-7">
                    <div class="input-group uta-input__wrapper uta-search">
                        <button class="btn btn-outline-secondary dropdown-toggle uta-search__filter-btn" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            {{ getTranslate[searchModeText] }}
                        </button>
                        <ul class="dropdown-menu uta-search__filter-menu">
                            <li class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('')">{{ getTranslate.CARS_SEARCH_MODE_ALL }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('vin')">{{ getTranslate.CARS_SEARCH_MODE_VIN }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('imei')">{{ getTranslate.CARS_SEARCH_MODE_IMEI }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('car_number')">{{ getTranslate.CARS_SEARCH_MODE_NUMBER }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('phone')">{{ getTranslate.CARS_SEARCH_MODE_PHONE }}</a>
                            </li>
                            <li  class="uta-search__filter-menu-item">
                                <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('iccid')">{{ getTranslate.CARS_SEARCH_MODE_ICCID }}</a>
                            </li>
                        </ul>
                        <input 
                            type="text" 
                            class="form-control uta-input__wrapper-input uta-search__input"
                            :placeholder="getTranslate.SEARCH + '...'" 
                            v-model="inputSearch"
                            @keyup.escape="inputSearch = '', fetchCars()"
                        >
                        <template v-if="inputSearch.length > 2">
                            <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = '', fetchCars()">
                                cancel
                            </span>
                        </template>
                        <template v-else>
                            <span class="material-icons uta-input__wrapper-icon">
                                search
                            </span>
                        </template>
                    </div>
                </div>
                <div class="d-none d-md-flex switcher switcher--text-left uta-cars__filter">
                    <input class="switcher__input uta-cars__filter-input" type="checkbox" id="UTAEcuFilter" v-model="onlyOnline" :checked="onlyOnline">
                    <label class="switcher__label uta-cars__filter-label" for="UTAEcuFilter">
                        <span class="switcher__text">{{ getTranslate.CARS_FILTER_ONLINE }}</span>
                    </label>
                </div>
                <div class="d-none d-md-flex view-selector">
                    <a href="#" class="view-selector__item view-selector__item--lines" @click.prevent="setViewMode('lines')"></a>
                    <a href="#" class="view-selector__item view-selector__item--grid" @click.prevent="setViewMode('grid')"></a>
                </div>
            </div>

            <div 
                class="my-4 uta-alert" 
                :class="{
                    'uta-alert--success'    : favoriteCarCreateMsg.success, 
                    'uta-alert--danger'     : !favoriteCarCreateMsg.success
                }"
                v-if="'success' in favoriteCarCreateMsg"
            >
                <div class="uta-alert__icon">
                    <span class="material-icons">
                        {{ favoriteCarCreateMsg.success ? 'check_circle' : 'error_outline' }}
                    </span>
                </div>
                <div class="uta-alert__text">
                    {{ favoriteCarCreateMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="favoriteCarsStore.setCreateFavoriteCarMsg({})">
                    <span class="material-icons">
                        close
                    </span>
                </button>
            </div>

            <div 
                class="my-4 uta-alert" 
                :class="{
                    'uta-alert--success'    : favoriteCarDeleteMsg.success, 
                    'uta-alert--danger'     : !favoriteCarDeleteMsg.success
                }"
                v-if="'success' in favoriteCarDeleteMsg"
            >
                <div class="uta-alert__icon">
                    <span class="material-icons">
                        {{ favoriteCarDeleteMsg.success ? 'check_circle' : 'error_outline' }}
                    </span>
                </div>
                <div class="uta-alert__text">
                    {{ favoriteCarDeleteMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="favoriteCarsStore.setDeleteFavoriteCarMsg({})">
                    <span class="material-icons">
                        close
                    </span>
                </button>
            </div>

            <template v-if="cars.array">
                
                <template v-if="viewMode == 'lines'">
                    <div class="container-fluid">
                        <div class="uta-cars__list uta-list">
                            <div class="uta-list__title d-none d-lg-block uta-cars__title">
                                <div class="row">
                                    <div class="col-10">
                                        <div class="row">
                                            <div class="col-12 col-lg-3">{{ getTranslate.CARS_IMEI }}</div>
                                            <div class="col-12 col-lg-3">{{ getTranslate.CARS_ONLINE }}</div>
                                            <div class="col-12 col-lg-3">{{ getTranslate.CARS_MODEL }}</div>
                                            <div class="col-12 col-lg-3">{{ getTranslate.CARS_VIN }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="uta-list__content">
                                <div 
                                    class="row align-items-center uta-list__item uta-cars__car uta-cars__car--lines" 
                                    :class="{ 'uta-list__item--dark' : index % 2 }" 
                                    v-for="(car, index) in cars.array" 
                                    :key="car.uuid"
                                >
                                    <div class="col-10">
                                        <div class="row">
                                            <div class="col-12 col-lg-3 uta-cars__car-vin">
                                                <span class="d-inline d-lg-none uta-cars__car-mobile-title">{{ getTranslate.CARS_IMEI }}: </span>
                                                <router-link :to="'/cars/' + car.uuid" class="uta-list__item-link uta-cars__link">
                                                    {{ car.data?.imei || '&mdash;' }}
                                                </router-link>
                                            </div>
                                            <div 
                                                class="col-12 col-md-3 uta-cars__car-status"
                                                :class="{
                                                    'uta-cars__car-status--offline'         : !('online' in car),
                                                    'uta-cars__car-status--offline'         : car.online && car.online.status == 0,
                                                    'uta-cars__car-status--nosatellites'    : car.online && car.online.status == 1,
                                                    'uta-cars__car-status--online'          : car.online && car.online.status == 2
                                                }"
                                            >
                                                <span class="d-inline d-lg-none uta-cars__car-mobile-title">{{ getTranslate.CARS_ONLINE }}: </span>
                                                <span class="uta-cars__car-status-text">
                                                    {{ getTranslate[car.online?.text || 'OFFLINE' ] }}
                                                </span>
                                            </div>
                                            <div class="col-12 col-lg-3 uta-cars__car-model">
                                                <span class="d-inline d-lg-none uta-cars__car-mobile-title">{{ getTranslate.CARS_MODEL }}: </span>
                                                {{ vinDecoder(car.data?.vin).model || '&mdash;' }}
                                            </div>
                                            <div class="col-12 col-lg-3 uta-cars__car-name">
                                                <span class="d-inline d-lg-none uta-cars__car-mobile-title">{{ getTranslate.CARS_VIN }}: </span>
                                                {{ car.data?.vin || '&mdash;' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-2 text-end">
                                        <template v-if="car.favorite">
                                            <div 
                                                :title="getTranslate.FAVORITE_CARS_ADD"
                                                class="uta-cars__car-favorite"
                                                @click="favoriteCarsStore.deleteFavoriteCar({ uuidCarArray: [ car.uuid ] }).then(() => fetchCars())"
                                            >
                                                <span class="material-icons">
                                                    star
                                                </span>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div 
                                                :title="getTranslate.FAVORITE_CARS_ADD"
                                                class="uta-cars__car-favorite"
                                                @click="favoriteCarsStore.createFavoriteCar({ uuidCarArray: [ car.uuid ] }).then(() => car.favorite = true)"
                                            >
                                                <span class="material-icons">
                                                    star_outline
                                                </span>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-if="viewMode == 'grid'">
                    <div class="row">
                        <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2" v-for="(car, index) in cars.array" :key="index">
                            <div class="uta-cars__car uta-cars__car--grid">
                                <router-link :to="'/cars/' + car.uuid" class="uta-cars__car-image">
                                    <UTACarImage :vin="car.data?.vin" />
                                </router-link>
                                <div v-if="!car.favorite" class="uta-cars__car-favorite" @click="favoriteCarsStore.createFavoriteCar({ uuidCarArray: [ car.uuid ] }).then(() => car.favorite = true)">
                                    <span class="material-icons">
                                        star_outline
                                    </span>
                                </div>
                                <div v-if="car.favorite" class="uta-cars__car-favorite" @click="favoriteCarsStore.deleteFavoriteCar({ uuidCarArray: [ car.uuid ] }).then(() => fetchCars())">
                                    <span class="material-icons">
                                        star
                                    </span>
                                </div>
                                <div class="uta-cars__car-vin">{{ car.data?.vin || '&mdash;' }}</div>
                                <div class="row">
                                    <div class="col-12">
                                        <div class="uta-cars__car-number">
                                            <span class="uta-cars__car-number-label">
                                                {{ getTranslate.CARS_IMEI }}: 
                                            </span> 
                                            <span class="uta-cars__car-number-value">
                                                {{ car.data?.imei }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <div class="uta-cars__car-model">
                                            <span class="uta-cars__car-model-label">
                                                {{ getTranslate.CARS_MODEL }}: 
                                            </span>
                                            <span class="uta-cars__car-model-value">
                                                {{ vinDecoder(car.data?.vin).model || '&mdash;' }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <div class="uta-cars__car-model">
                                            <span class="uta-cars__car-model-label">
                                                {{ getTranslate.CARS_NAME }}: 
                                            </span>
                                            <span class="uta-cars__car-model-value">
                                                {{ car.name || '&mdash;' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-4 d-flex justify-content-between">
                                    <div class="d-flex align-items-center">
                                        <div 
                                            class="uta-cars__car-status"
                                            :class="{
                                                'uta-cars__car-status--offline'         : !('online' in car),
                                                'uta-cars__car-status--offline'         : car.online && car.online.status == 0,
                                                'uta-cars__car-status--nosatellites'    : car.online && car.online.status == 1,
                                                'uta-cars__car-status--online'          : car.online && car.online.status == 2
                                            }"
                                        >
                                            <span class="uta-cars__car-status-text">
                                                {{ getTranslate[car.online?.text || 'OFFLINE'] }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-end">
                                        <router-link :to="'/cars/' + car.uuid" class="uta-cars__car-readmore">
                                            {{ getTranslate.CARS_READMORE }} 
                                            <span class="material-icons">
                                                navigate_next
                                            </span>
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
            <template v-else-if="'success' in carsMsg">
                <div 
                    class="my-4 uta-alert" 
                    :class="{
                        'uta-alert--success'    : carsMsg.success, 
                        'uta-alert--danger'     : !carsMsg.success
                    }"
                    v-if="'success' in carsMsg"
                >
                    <div class="uta-alert__icon">
                        <span class="material-icons">
                            {{ carsMsg.success ? 'check_circle' : 'error_outline' }}
                        </span>
                    </div>
                    <div class="uta-alert__text">
                        {{ getTranslate[carsMsg.text] || carsMsg.text }}
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="uta-alert uta-alert--danger">
                    {{ getTranslate.CARS_EMPTY }}
                </div>
            </template>
            <div class="uta-cars__pagination" v-if="cars.c && (cars.c.isBefore || cars.c.isAfter)">
                <div class="row">
                    <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                        <div class="uta-pagination">
                            <button 
                                :disabled="carsProcess || !cars.c.isBefore"
                                class="uta-btn uta-btn--sm uta-pagination__prev" 
                                @click.prevent="changePage(cars.c.start, 'before')"
                            >
                                <span class="material-icons">
                                    navigate_before
                                </span>
                            </button>
                            <div class="uta-pagination__current">
                                {{ pageNumber }}
                            </div>
                            <button 
                                :disabled="carsProcess || !cars.c.isAfter"
                                class="uta-btn uta-btn--sm uta-pagination__next" 
                                @click.prevent="changePage(cars.c.stop, 'after')"
                            >
                                <span class="material-icons">
                                    navigate_next
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <transition name="bounce">
            <Modal 
                v-if="showCarsCreate"
                :header="
                    checkArrayIncludes(profile.attr?.array, ['CREATOR_CAR', 'SUPER_ADMIN']) ? 
                        getTranslate.CARS_CREATE_HEADER : 
                        getTranslate.CARS_ADD_HEADER
                    "
                :size="
                    checkArrayIncludes(profile.attr?.array, ['CREATOR_CAR', 'SUPER_ADMIN']) ? 
                        'lg' : 
                        'md'
                "
                @closeModal="showCarsCreate = false"
            >
                <template v-slot:body>
                    <UTACarsCreate @closeModal="showCarsCreate = false" :activate="checkArrayIncludes(profile.attr?.array, ['CREATOR_CAR', 'SUPER_ADMIN']) == false" />
                </template>
            </Modal>
        </transition>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/cars';
</style>